///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, input, Input, OnDestroy, OnInit } from '@angular/core';
import {
    SuiAbstractControl,
    SuiControlValidationDirective,
    SuiStringControlComponent,
    SuiTooltipDirective,
    SuiTypeMultipleSelect,
    SuiTypeString
} from '@siklu/sui-lib/infrastructure';
import { E2eTestUtils } from '../../../../testing/e2e-test-utils';
import { NgClass } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { ActionIconDirectivesModule } from '../../directives/action-icon-directives/action-icon-directives.module';
import { Subject, takeUntil } from 'rxjs';
import { ButtonsDirectivesModule } from '../../directives/buttons-directives/buttons-directives.module';
import { SuiTranslatePipe } from '@siklu/sui-lib/localization';
import { SuiSvgIconComponent } from '@siklu/sui-lib/components/svg-icon';

enum MultiSelectListType {
    AVAILABLE = 'available',
    SELECTED = 'selected'
}

@Component({
    selector: 'winde-multiple-select',
    templateUrl: './sui-winde-multiple-select.component.html',
    styleUrls: ['./sui-winde-multiple-select.component.scss'],
    imports: [
        NgClass,
        CdkDropListGroup,
        CdkDropList,
        CdkDrag,
        ActionIconDirectivesModule,
        ButtonsDirectivesModule,
        SuiSvgIconComponent,
        SuiTranslatePipe,
        SuiTooltipDirective,
        SuiStringControlComponent
    ],
    hostDirectives: [
        {
            directive: SuiControlValidationDirective,
            inputs: ['controlConfig: config']
        }
    ]
})
export class MultipleSelectComponent<T> extends SuiAbstractControl<SuiTypeMultipleSelect<T>> implements OnInit, OnDestroy {
    @Input() config: SuiTypeMultipleSelect<T>;
    public displayName = input<string>('name');

    public readonly searchControl = new SuiTypeString('');
    public readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();
    public readonly multiSelectListType = MultiSelectListType;

    public readyToBeSelected = new Set<T>();
    public readyToBeDeselected = new Set<T>();

    private readonly destroy$: Subject<boolean> = new Subject<boolean>();

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.searchControl.placeholder = 'Search...';
        this.searchControl.valueChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.readyToBeSelected.clear();
            this.config.filterAvailableItems(this.searchControl.getValue(), this.displayName());
        });
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    availableClick(item: T): void {
        if (this.config.readonly) {
            return;
        }
        if (this.readyToBeSelected.has(item)) {
            this.readyToBeSelected.delete(item);
        } else {
            this.readyToBeSelected.add(item);
        }
    }

    selectedClick(item: T): void {
        if (this.config.readonly) {
            return;
        }
        if (this.readyToBeDeselected.has(item)) {
            this.readyToBeDeselected.delete(item);
        } else {
            this.readyToBeDeselected.add(item);
        }
    }

    select(): void {
        this.config.toggleItems(Array.from(this.readyToBeSelected));
        this.readyToBeSelected.clear();
    }

    deselect(): void {
        this.config.toggleItems(Array.from(this.readyToBeDeselected));
        this.readyToBeDeselected.clear();
    }

    drop(event: CdkDragDrop<T[], T>): void {
        if (event.container.id === MultiSelectListType.SELECTED) {
            this.config.selectItemToSpecificPosition(event.item.data, event.currentIndex);
        } else {
            this.config.deselectItemToSpecificPosition(event.item.data, event.currentIndex);
        }
    }
}
